/**
 * usePriorVisitsBadge(schedules)
 *
 * For each row in the visible schedule, returns whether that visit has at
 * least one EARLIER visit on file for the same patient. Used by
 * PatientListPanel to render a small "prior visits" icon next to active rows
 * so a clinician can glance at the sidebar and see which patients they've
 * seen before.
 *
 * "Prior visit" matches the PriorVisitsModal definition: another notes-c row
 * with the same `EHR_ID` and a `date_` strictly less than the schedule row's
 * own `date_`. Future / same-day visits are not counted.
 *
 * Implementation:
 *   1. Collect the unique (EHR_ID, schema) pairs from the schedule — same
 *      fan-out strategy `useTodaysSchedule` uses so cross-schema linked
 *      providers get prior visits from their home tenant too.
 *   2. One Supabase REST query per schema with
 *      `EHR_ID=in.(...)&date_.lt.<today>&select=EHR_ID,date_` — the
 *      smallest payload that answers "is there any earlier row for this
 *      patient in this schema".
 *   3. Build a Map<EHR_ID, Set<date_string>> and let the caller check
 *      `hasPriorVisit(row)`.
 *
 * Returns: { hasPriorVisit(row) -> boolean, isLoading }
 *
 * Roster rows (`patients_with_ehrid`) aren't date-keyed and have no
 * historical visits — the badge is suppressed for those. Fixture patients
 * are checked against real notes-c just like any other row.
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { supabaseUrl, supabaseKey } from '../../components/env';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { fetchEffectiveUserRow } from '../../utils/effectiveUserRole';
import { hasClinicalAdminPermissions, isPatientRole } from '../../config/roles';

function getSchedulesEndpoint() {
  const base = supabaseUrl || '';
  return `${base}/rest/v1/notes-c`;
}

// Local-date ISO (YYYY-MM-DD). Mirrors MedStudentClinicPage.todayIso —
// new Date().toISOString() returns UTC and shifts to the wrong day for
// users west of UTC after their local midnight.
function todayIsoLocal() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function buildHeaders(token, schema) {
  return {
    apikey: supabaseKey || '',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    Accept: 'application/json',
    ...(schema && schema !== 'public'
      ? { 'Accept-Profile': schema }
      : { 'Accept-Profile': 'public' }),
  };
}

export function usePriorVisitsBadge(schedules) {
  // EHR_ID -> Set<date_string> of earlier visits we know about.
  const [priorByEhr, setPriorByEhr] = useState(() => new Map());
  const [isLoading, setIsLoading] = useState(false);

  // Stable signature so we only refetch when the actual schedule contents
  // change, not every render (the parent passes a fresh array reference
  // every paint).
  const signature = (Array.isArray(schedules) ? schedules : [])
    .filter((s) => s && s.EHR_ID && s.date_)
    .map((s) => `${s._schema || ''}::${s.EHR_ID}::${s.date_}`)
    .sort()
    .join('|');

  useEffect(() => {
    let cancelled = false;
    const rows = Array.isArray(schedules) ? schedules : [];
    const dateable = rows.filter((s) => s && s.EHR_ID && s.date_);

    if (dateable.length === 0) {
      setPriorByEhr(new Map());
      setIsLoading(false);
      return () => { cancelled = true; };
    }

    // Group EHR_IDs by the schema they should be queried from — mirrors the
    // fan-out in useTodaysSchedule. A clinical user with selected providers
    // sees priors from every ticked schema; everyone else stays on home.
    const bySchema = new Map();
    for (const s of dateable) {
      const schema = s._schema || supabaseSchema;
      if (!bySchema.has(schema)) bySchema.set(schema, new Set());
      bySchema.get(schema).add(s.EHR_ID);
    }

    (async () => {
      setIsLoading(true);
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token ?? null;
        const userId = session?.user?.id ?? null;
        let role = null;
        if (userId && !isPatientRole(null)) {
          try {
            const userRes = await fetchEffectiveUserRow({ id: userId });
            role = userRes?.data?.role ?? null;
          } catch (_) { /* keep role null → fall back to home schema */ }
        }
        // For now, query exactly the schemas the schedule rows claim to
        // belong to. hasClinicalAdminPermissions role isn't expanded here
        // because the schedule rows themselves are already gated upstream
        // by useTodaysSchedule's fan-out — adding to it here would only
        // broaden the badge beyond what the sidebar already showed.
        const today = todayIsoLocal();
        const out = new Map();

        await Promise.all([...bySchema.entries()].map(async ([schema, ids]) => {
          const idList = [...ids];
          if (idList.length === 0) return;
          try {
            // PostgREST `in.` filter — wrap each id in parens to escape commas
            // and quotes that EHR_IDs may carry. Empty filter would 400.
            const inExpr = `(${idList.map((id) => `"${String(id).replace(/"/g, '\\"')}"`).join(',')})`;
            const res = await axios.get(getSchedulesEndpoint(), {
              headers: buildHeaders(token, schema),
              params: {
                EHR_ID: `in.${inExpr}`,
                'date_': `lt.${today}`,
                select: 'EHR_ID,date_',
                limit: 1000,
              },
            });
            const data = Array.isArray(res.data) ? res.data : [];
            for (const r of data) {
              if (!r || !r.EHR_ID || !r.date_) continue;
              if (!out.has(r.EHR_ID)) out.set(r.EHR_ID, new Set());
              out.get(r.EHR_ID).add(r.date_);
            }
          } catch (e) {
            // One schema failing shouldn't empty the whole badge set; the
            // hook degrades to "no prior visits" for EHR_IDs in that schema,
            // matching the useTodaysSchedule warn-and-continue policy.
            console.warn('[usePriorVisitsBadge] schema', schema, 'failed:', e?.message || e);
          }
        }));

        if (!cancelled) {
          setPriorByEhr(out);
          setIsLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setPriorByEhr(new Map());
          setIsLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [signature]);

  const hasPriorVisit = useCallback(
    (row) => {
      if (!row || !row.EHR_ID || !row.date_) return false;
      const dates = priorByEhr.get(row.EHR_ID);
      if (!dates || dates.size === 0) return false;
      // Strip to date-only ISO so string comparison lines up with what the
      // REST query stored. notes-c date_ is YYYY-MM-DD.
      const target = String(row.date_).slice(0, 10);
      for (const d of dates) {
        if (String(d).slice(0, 10) < target) return true;
      }
      return false;
    },
    [priorByEhr],
  );

  return { hasPriorVisit, isLoading };
}